<template>
	<div class="help-wrapper" :class="{ 'help-wrapper--icon': !hasSlot }" :data-label="label">
		<button
			type="button"
			:popovertarget="id"
			class="no-btn help-wrapper__trigger"
			:class="{ 'help-wrapper__trigger--not-ready': !isHelpAvailable }"
			@click.stop
		>
			<template v-if="hasSlot">
				<slot />
			</template>
			<template v-else>
				<i v-if="isHelpAvailable" class="help-wrapper__icon fas fa-question-circle" />
			</template>
		</button>
		<div
			:id="id"
			ref="popover"
			class="help-wrapper__tooltip"
			:class="{ 'help-wrapper__tooltip--icon': !hasSlot }"
			popover
			@beforetoggle="handleToggle"
		>
			<div v-if="title" class="help-wrapper__header">
				<div class="help-wrapper__title" v-text="title" />
				<button type="button" class="no-btn help-wrapper__close" @click.stop="handleClose">
					<i class="fas fa-times" />
				</button>
			</div>
			<div
				class="help-wrapper__content"
				v-html="content || '<p>No help content to show.</p>'"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import type { HttpHelper } from '../helper/http';
	import { computed, inject, ref, useId, useSlots, useTemplateRef } from 'vue';

	const props = defineProps<{
		label: string,
	}>();

	// Shared across every help element on the page, so a label already fetched by one of them is not
	// requested again by another, and reopening the same label costs nothing.
	const cache = new Map<string, { title: string, content: string }>();

	const http = inject<HttpHelper>('http')!;
	const help = inject<Record<string, any>>('help')!;
	const slots = useSlots();
	const popover = useTemplateRef<HTMLDivElement>('popover');
	const id = ref(`help-wrapper-${useId()}`);
	const title = ref('Loading...');
	const content = ref('<p>Please Wait</p>');

	const isHelpAvailable = computed(() => {
		if (props.label && props.label !== '' && props.label !== 'undefined') {
			if (help && help[props.label]) return true;
		}

		return false;
	});

	const hasSlot = computed(() => {
		return Object.keys(slots).length > 0;
	});

	// The tooltip is positioned by CSS anchor positioning (see help.scss), so it stays with its trigger
	// while the page scrolls or its content changes, without any positioning here.
	const handleToggle = (event: ToggleEvent) => {
		if (event.newState !== 'open' || !isHelpAvailable.value) return;

		requestHelp();
	};

	const handleClose = () => {
		popover.value?.hidePopover();
	};

	const requestHelp = async () => {
		const label = props.label;
		const cached = cache.get(label);

		if (cached) {
			title.value = cached.title;
			content.value = cached.content;

			return;
		}

		// Clear first, otherwise the previous label's content shows until the new one arrives.
		title.value = 'Loading...';
		content.value = '<p>Please Wait</p>';

		const response = await http.get(`/api/help/${label}`);

		if (response.status !== 200) return;

		const result = await response.json();
		const entry = {
			title: result.title || 'No help found',
			content: result.content || '',
		};

		cache.set(label, entry);

		// The label may have changed again while this was in flight, in which case this response is stale.
		if (props.label !== label) return;

		title.value = entry.title;
		content.value = entry.content;
	};
</script>
